/**
 * View-intent -> the app's own API, answered with NO agent turn.
 *
 * Every one of these is a read of data the account already owns, and the
 * frontend has always been able to fetch it. Routing them here removes a whole
 * turn: the ~29k-token cached prefix (system prompt + 172 tool declarations) is
 * re-read on every round of a turn, so the cheapest turn is the one that never
 * happens.
 *
 * Deliberately rendered as `summary_card` blocks, which already exist. Three
 * bespoke renderers would be three things to maintain for no gain — the answers
 * are all label/value rows.
 *
 * NOT here, on purpose: finding companies or contacts, and anything that
 * changes data. Those need the model.
 */

import { makeApi, URLS } from "Utils/apiURL";
import userDetail from "Stores/userDetail";

const num = value => (typeof value === "number" && isFinite(value) ? value : null);

const asArray = data => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  // CM wraps list payloads under a few different keys depending on the era of
  // the endpoint; take the first array we find rather than guessing one.
  const key = Object.keys(data).find(k => Array.isArray(data[k]));
  return key ? data[key] : [];
};

// Response bodies vary by endpoint vintage: some return the payload directly,
// others nest it under response/data.
const unwrap = res => {
  const body = (res && res.data) || {};
  return body.response || body.data || body;
};

// Matches the reports page's default view, so the copilot's headline numbers
// and the Reports screen agree.
const REPORT_WINDOW_DAYS = 30;

const pct = (part, whole) =>
  whole ? `${Math.round((part / whole) * 1000) / 10}%` : "—";

export const DIRECT_READS = {
  connected_inboxes: {
    label: "Connected inboxes",
    request: () => ({ url: URLS.getConnectedInboxes, method: "GET" }),
    toCard: res => {
      const rows = asArray(unwrap(res));
      if (!rows.length) {
        return {
          title: "Connected inboxes",
          rows: [{ label: "Connected", value: "None yet" }],
          footnote: "Connect an inbox in Settings to start sending."
        };
      }
      const disconnected = rows.filter(
        r => r.disconnected === true || r.status === "DISCONNECTED"
      );
      const byProvider = {};
      rows.forEach(r => {
        const provider = r.type || r.provider || r.inboxType || "Other";
        byProvider[provider] = (byProvider[provider] || 0) + 1;
      });
      return {
        title: "Connected inboxes",
        rows: [
          { label: "Connected", value: String(rows.length) },
          ...Object.entries(byProvider).map(([provider, count]) => ({
            label: provider,
            value: String(count)
          })),
          ...(disconnected.length
            ? [
                {
                  label: "Needs reconnecting",
                  value: String(disconnected.length),
                  tone: "warn"
                }
              ]
            : [])
        ],
        footnote: rows
          .map(r => r.email || r.emailId || r.name)
          .filter(Boolean)
          .slice(0, 6)
          .join(", ")
      };
    }
  },

  inbox_usage: {
    label: "Inbox sending limits",
    // The daily send allowance is NOT on /cm/seq/inbox/usage — that endpoint
    // returns {inboxId, email, totalSequences, activeSequences}, i.e. how many
    // sequences each inbox is used by. The real allowance is already in the
    // app's own store as userFeatureFlag.{emailSendLimit,emailSentCount}, the
    // same pair DashboardModule uses to raise the limit banner. So the quota
    // needs no request at all; the fetch only adds per-inbox context.
    request: () => ({ url: URLS.getInboxUsage, method: "GET" }),
    toCard: res => {
      const inboxes = asArray(unwrap(res));
      const flags = userDetail.userFeatureFlag || {};
      const limit = num(flags.emailSendLimit) ?? 0;
      // Seeded at -1 before the first fetch resolves; that is "unknown", not 0.
      const sentRaw = num(flags.emailSentCount);
      const sent = sentRaw === null || sentRaw < 0 ? null : sentRaw;

      const quotaRows =
        limit && sent !== null
          ? [
              { label: "Sent today", value: `${sent} of ${limit}` },
              {
                label: "Remaining today",
                value: String(Math.max(0, limit - sent)),
                ...(sent >= limit ? { tone: "warn" } : {})
              }
            ]
          : [];

      const inboxRows = inboxes.slice(0, 8).map(r => ({
        label: r.email || r.inboxId || "Inbox",
        value: `${num(r.activeSequences) ?? 0} active ${
          (num(r.activeSequences) ?? 0) === 1 ? "sequence" : "sequences"
        }`
      }));

      if (!quotaRows.length && !inboxRows.length) {
        return {
          title: "Inbox sending limits",
          rows: [{ label: "Usage", value: "No sending activity yet" }]
        };
      }

      return {
        title: "Inbox sending limits",
        rows: [...quotaRows, ...inboxRows],
        footnote: quotaRows.length
          ? `${pct(sent, limit)} of today's account limit used`
          : "Daily limit unavailable — showing per-inbox sequence usage."
      };
    }
  },

  sequence_reports: {
    label: "Sequence performance",
    // fromDate/toDate are primitive `long` on ReportsPayload, so an empty body
    // is not "no filter" — it is the epoch, and the aggregation returns a
    // 1970-to-1970 window with nothing in it. Send a real window, as the
    // reports page does.
    request: () => ({
      url: URLS.getSequenceReports,
      method: "POST",
      data: {
        pageNo: 1,
        limit: 100,
        fromDate: Date.now() - REPORT_WINDOW_DAYS * 24 * 60 * 60 * 1000,
        toDate: Date.now(),
        memberIds: []
      }
    }),
    toCard: res => {
      const body = unwrap(res);
      // constructSequenceReport puts the rows under `sequenceReport`.
      const rows = asArray(body.sequenceReport || body);
      // CM's keys are emails*Count, not sent/open/click/replyCount — the old
      // names summed to zero on every account.
      const totals = rows.reduce(
        (acc, r) => ({
          sent: acc.sent + (num(r.emailsSentCount) ?? 0),
          opened: acc.opened + (num(r.emailsOpenCount) ?? 0),
          clicked: acc.clicked + (num(r.emailsClickCount) ?? 0),
          replied: acc.replied + (num(r.emailsReplyCount) ?? 0)
        }),
        { sent: 0, opened: 0, clicked: 0, replied: 0 }
      );
      if (!totals.sent) {
        return {
          title: "Sequence performance",
          rows: [
            {
              label: "Sent",
              value: `Nothing sent in the last ${REPORT_WINDOW_DAYS} days`
            }
          ]
        };
      }
      const active = rows.filter(r => (num(r.emailsSentCount) ?? 0) > 0).length;
      return {
        title: "Sequence performance",
        rows: [
          { label: "Sent", value: totals.sent.toLocaleString("en-US") },
          { label: "Open rate", value: pct(totals.opened, totals.sent) },
          { label: "Click rate", value: pct(totals.clicked, totals.sent) },
          { label: "Reply rate", value: pct(totals.replied, totals.sent) }
        ],
        footnote: `Last ${REPORT_WINDOW_DAYS} days, across ${active} sending sequence${
          active === 1 ? "" : "s"
        }`
      };
    }
  }
};

/**
 * Fetch and shape one intent. Throws on failure so the caller can fall back to
 * a normal turn — a broken direct read must never become a dead end.
 */
export const runDirectRead = async intent => {
  const spec = DIRECT_READS[intent];
  if (!spec) throw new Error(`unknown direct read: ${intent}`);
  const res = await makeApi(spec.request());
  return spec.toCard(res);
};

export default DIRECT_READS;
